import { z } from "zod";
import {
  CookSessionApiItemSchema,
  CreateCookSessionRequestSchema,
  PatchCookSessionRequestSchema,
  WaitCookSessionRequestSchema,
  CompleteCookSessionRequestSchema,
  type CookSessionApiItem,
  type CreateCookSessionRequest,
  type PatchCookSessionRequest,
  type WaitCookSessionRequest,
  type CompleteCookSessionRequest,
} from "@recipevault/shared";
import { apiGet, apiPost, apiPatch } from "../../shared/http.js";

const SessionEnvelope = z.object({ session: CookSessionApiItemSchema });
const ListEnvelope = z.object({
  items: z.array(CookSessionApiItemSchema),
  nextCursor: z.string().nullable().optional(),
});

export async function createCookSession(
  body: CreateCookSessionRequest,
  idempotencyKey: string,
): Promise<CookSessionApiItem> {
  CreateCookSessionRequestSchema.parse(body);
  const raw = await apiPost<unknown>("/cook-sessions", body, { idempotencyKey });
  return SessionEnvelope.parse(raw).session;
}

export async function getOpenCookSessions(status?: "active" | "paused" | "waiting"): Promise<CookSessionApiItem[]> {
  const qs = status ? `?status=${status}` : "";
  const raw = await apiGet<unknown>(`/cook-sessions${qs}`);
  return ListEnvelope.parse(raw).items;
}

export async function getCookSession(id: string): Promise<CookSessionApiItem> {
  const raw = await apiGet<unknown>(`/cook-sessions/${id}`);
  return SessionEnvelope.parse(raw).session;
}

export async function patchCookSession(id: string, body: PatchCookSessionRequest): Promise<CookSessionApiItem> {
  PatchCookSessionRequestSchema.parse(body);
  const raw = await apiPatch<unknown>(`/cook-sessions/${id}`, body);
  return SessionEnvelope.parse(raw).session;
}

export async function waitCookSession(id: string, body: WaitCookSessionRequest): Promise<CookSessionApiItem> {
  WaitCookSessionRequestSchema.parse(body);
  const raw = await apiPost<unknown>(`/cook-sessions/${id}/wait`, body);
  return SessionEnvelope.parse(raw).session;
}

export async function resumeCookSession(id: string): Promise<CookSessionApiItem> {
  const raw = await apiPost<unknown>(`/cook-sessions/${id}/resume`, {});
  return SessionEnvelope.parse(raw).session;
}

export async function pauseCookSession(id: string): Promise<CookSessionApiItem> {
  const raw = await apiPost<unknown>(`/cook-sessions/${id}/pause`, {});
  return SessionEnvelope.parse(raw).session;
}

export async function completeCookSession(id: string, body: CompleteCookSessionRequest): Promise<CookSessionApiItem> {
  CompleteCookSessionRequestSchema.parse(body);
  const raw = await apiPost<unknown>(`/cook-sessions/${id}/complete`, body);
  return SessionEnvelope.parse(raw).session;
}

export async function abandonCookSession(id: string): Promise<void> {
  await apiPost<unknown>(`/cook-sessions/${id}/abandon`, {});
}
